import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Storefront } from '@phosphor-icons/react';

const STATUSES = ['', 'pending', 'approved', 'rejected', 'suspended'];

export default function AdminSellers() {
  const [sellers, setSellers] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [editCommission, setEditCommission] = useState(null); // {seller, value}
  const [kycOpen, setKycOpen] = useState({}); // id -> bool

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/sellers/admin/all', { params: filter ? { status: filter } : {} });
      setSellers(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const update = async (s, patch) => {
    try {
      await api.patch(`/sellers/admin/${s.id}`, patch);
      toast.success('Updated');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const approve = (s) => update(s, { status: 'approved' });
  const reject = (s) => {
    const reason = window.prompt('Rejection reason');
    if (reason === null) return;
    update(s, { status: 'rejected', rejected_reason: reason });
  };
  const suspend = (s) => update(s, { status: 'suspended' });
  const reactivate = (s) => update(s, { status: 'approved' });

  return (
    <div className="space-y-5" data-testid="admin-sellers">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl text-stone-900">Vendors</h1><p className="text-sm text-stone-500">{sellers.length} shown</p></div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {STATUSES.map((s) => (
          <button key={s||'all'} onClick={() => setFilter(s)} data-testid={`admin-seller-tab-${s||'all'}`}
            className={`h-8 px-3 rounded-full text-xs font-semibold ${filter === s ? 'bg-terracotta-600 text-white' : 'bg-white border border-stone-300 text-stone-700 hover:bg-stone-50'}`}>
            {s ? s.toUpperCase() : 'ALL'}
          </button>
        ))}
      </div>

      {loading ? <p className="text-stone-500">Loading…</p> : sellers.length === 0 ? (
        <div className="bg-white border border-dashed border-stone-200 rounded-2xl p-10 text-center text-stone-500">No vendors.</div>
      ) : (
        <div className="space-y-3">
          {sellers.map((s) => (
            <div key={s.id} data-testid={`admin-seller-${s.id}`} className="bg-white rounded-2xl border border-stone-200 p-4">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="w-11 h-11 rounded-xl bg-cream-100 grid place-items-center text-olive-700"><Storefront size={22}/></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-heading text-lg text-stone-900">{s.shop_name}</p>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase ${s.status === 'approved' ? 'bg-olive-100 text-olive-800' : s.status === 'rejected' ? 'bg-rose-100 text-rose-700' : s.status === 'suspended' ? 'bg-stone-200 text-stone-700' : 'bg-amber-100 text-amber-800'}`}>{s.status}</span>
                  </div>
                  <p className="text-sm text-stone-600">{s.owner_name} · {s.email} · {s.mobile}</p>
                  <p className="text-xs text-stone-500">PIN {s.pincode} · {s.address || '—'}</p>
                  <p className="text-xs text-stone-500 mt-1">Commission override: {s.commission_pct || 0}% {!s.commission_pct && <span className="text-stone-400">(uses platform default)</span>}</p>
                  {s.rejected_reason && <p className="text-xs text-rose-600 mt-1">Reason: {s.rejected_reason}</p>}
                  <button
                    type="button"
                    data-testid={`admin-seller-kyc-toggle-${s.id}`}
                    onClick={() => setKycOpen((o) => ({ ...o, [s.id]: !o[s.id] }))}
                    className="mt-2 text-xs text-olive-700 font-semibold hover:underline"
                  >
                    {kycOpen[s.id] ? 'Hide KYC & bank' : 'View KYC & bank details'}
                  </button>
                  {kycOpen[s.id] && (
                    <div data-testid={`admin-seller-kyc-${s.id}`} className="mt-3 bg-cream-50 border border-stone-200 rounded-xl p-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-stone-700">
                      <KV k="Account holder" v={s.account_holder_name}/>
                      <KV k="Account number" v={s.bank_account_number ? `••••${(s.bank_account_number || '').slice(-4)}` : ''}/>
                      <KV k="IFSC" v={s.ifsc}/>
                      <KV k="GSTIN" v={s.gstin}/>
                      <KV k="UPI ID" v={s.upi_id}/>
                      <KV k="Terms accepted" v={s.terms_accepted ? 'Yes' : 'No'}/>
                      {s.shop_photo_url && (
                        <div className="sm:col-span-2 mt-2">
                          <p className="text-[11px] uppercase text-stone-500">Shop photo</p>
                          <a href={s.shop_photo_url} target="_blank" rel="noopener noreferrer">
                            <img src={s.shop_photo_url} alt="shop" className="mt-1 h-24 rounded-lg border border-stone-200"/>
                          </a>
                        </div>
                      )}
                      {s.kyc_doc_url && (
                        <div className="sm:col-span-2 mt-2">
                          <p className="text-[11px] uppercase text-stone-500">KYC document</p>
                          <a href={s.kyc_doc_url} target="_blank" rel="noopener noreferrer" className="text-olive-700 font-semibold text-xs hover:underline break-all">{s.kyc_doc_url}</a>
                        </div>
                      )}
                      {s.upi_qr_url && (
                        <div className="sm:col-span-2 mt-2">
                          <p className="text-[11px] uppercase text-stone-500">UPI QR</p>
                          <a href={s.upi_qr_url} target="_blank" rel="noopener noreferrer">
                            <img src={s.upi_qr_url} alt="upi qr" className="mt-1 h-24 rounded-lg border border-stone-200 bg-white"/>
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {s.status === 'pending' && (
                    <>
                      <button data-testid={`admin-approve-${s.id}`} onClick={() => approve(s)} className="h-9 px-4 rounded-full bg-olive-700 text-white text-xs font-semibold hover:bg-olive-800">Approve</button>
                      <button data-testid={`admin-reject-${s.id}`} onClick={() => reject(s)} className="h-9 px-4 rounded-full bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700">Reject</button>
                    </>
                  )}
                  {s.status === 'approved' && (
                    <>
                      <button onClick={() => setEditCommission({ seller: s, value: s.commission_pct || 0 })} className="h-9 px-3 rounded-full border border-stone-300 text-xs font-semibold hover:bg-stone-50">Set commission</button>
                      <button data-testid={`admin-suspend-${s.id}`} onClick={() => suspend(s)} className="h-9 px-4 rounded-full bg-stone-800 text-white text-xs font-semibold hover:bg-stone-900">Suspend</button>
                    </>
                  )}
                  {s.status === 'suspended' && <button onClick={() => reactivate(s)} className="h-9 px-4 rounded-full bg-olive-700 text-white text-xs font-semibold hover:bg-olive-800">Reactivate</button>}
                  {s.status === 'rejected' && <button onClick={() => approve(s)} className="h-9 px-4 rounded-full bg-olive-700 text-white text-xs font-semibold hover:bg-olive-800">Approve now</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editCommission && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setEditCommission(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-sm p-5">
            <h3 className="font-heading text-lg text-stone-900">Set vendor commission</h3>
            <p className="text-xs text-stone-500 mt-1">{editCommission.seller.shop_name}. Set 0 to use platform default.</p>
            <input type="number" min="0" max="50" step="0.1" value={editCommission.value}
              onChange={(e) => setEditCommission({ ...editCommission, value: e.target.value })}
              className="w-full h-11 px-3 mt-3 border border-stone-200 rounded-xl text-sm"/>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setEditCommission(null)} className="h-9 px-4 rounded-full border border-stone-300 text-xs">Cancel</button>
              <button onClick={() => { update(editCommission.seller, { commission_pct: Number(editCommission.value) }); setEditCommission(null); }} className="h-9 px-4 rounded-full bg-terracotta-600 text-white text-xs font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <span className="text-stone-500">{k}: </span>
      <span className="font-medium text-stone-800 break-all">{v || <span className="text-stone-400">—</span>}</span>
    </div>
  );
}
